import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {NXKMER_PATTERN_RE} from 'molbio/codon-optimization/CodonOptimizationConstants';
import {getInvalidPatternError} from 'molbio/codon-optimization/CodonOptimizationUtil';
import {PatternToReduce} from 'molbio/codon-optimization/reduceCodonOptimizationTab';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, ORANGE, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import PopoverIcon from 'ui-library/PopoverIcon';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

interface CodonOptimizationReducePatternPickerProps {
  onAdd: () => void;
  onDelete: (patternIdx: number) => void;
  onDeleteAll: () => void;
  onUpdate: (patternIdx: number, patternToReduce: PatternToReduce) => void;
  rows: Array<PatternToReduce>;
}

export default function CodonOptimizationReducePatternPicker(
  props: CodonOptimizationReducePatternPickerProps
): JSX.Element {
  return (
    <div>
      <Table
        numRows={props.rows.length}
        emptyTableText="Add a pattern to reduce its usage, using the button below"
      >
        <Column
          header={<Cell className={css(styles.validation)} />}
          renderCell={(rowIndex: number) => {
            const error = getInvalidPatternError(props.rows[rowIndex]);
            return error != null ? (
              <Cell className={css(styles.validation)}>
                <PopoverIcon className={css(styles.warningIcon)} renderContent={() => error} icon="warning" />
              </Cell>
            ) : (
              <Cell className={css(styles.validation)} />
            );
          }}
        />
        <Column
          header={<Cell>Pattern</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              <TextInput
                placeholder="Pattern"
                onChange={(e) => {
                  const isRepeatPattern = NXKMER_PATTERN_RE.test(e.target.value);
                  props.onUpdate(rowIndex, {
                    pattern: e.target.value,
                    reverseComplement: isRepeatPattern ? false : props.rows[rowIndex].reverseComplement,
                  });
                }}
                size={Size.MEDIUM}
                value={props.rows[rowIndex].pattern || ''}
              />
            </Cell>
          )}
        />
        <Column
          header={<Cell className={css(styles.centeredTableHeader)}>Reverse complement</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell className={css(styles.tableCell)}>
              <input
                type="checkbox"
                onChange={() => {
                  props.onUpdate(rowIndex, {
                    pattern: props.rows[rowIndex].pattern,
                    reverseComplement: !props.rows[rowIndex].reverseComplement,
                  });
                }}
                checked={props.rows[rowIndex].reverseComplement}
                disabled={NXKMER_PATTERN_RE.test(props.rows[rowIndex].pattern)}
              />
            </Cell>
          )}
        />
        <Column
          header=""
          renderCell={(rowIndex: number) => (
            <Cell className={css(styles.tableCell)}>
              <BenchlingIcon
                onClick={() => {
                  props.onDelete(rowIndex);
                }}
                icon="delete"
                className={css(styles.deleteButton)}
              />
            </Cell>
          )}
        />
      </Table>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
        onClick={props.onAdd}
        size={ButtonSize.SMALL}
        text={
          <div
            {...getTestProps({
              component: 'CodonOptimizationReducePatternPicker',
              element: 'addPatternButton',
            })}
          >
            <BenchlingIcon icon="create" />
            <span className={css(styles.buttonText)}>Add pattern to reduce</span>
          </div>
        }
      />
      <Button
        buttonStyle={ButtonStyle.NEGATIVE}
        className={css(styles.deleteAll)}
        disabled={props.rows.length === 0}
        onClick={props.onDeleteAll}
        size={ButtonSize.SMALL}
        text={
          <div>
            <BenchlingIcon icon="delete" />
            <span className={css(styles.buttonText)}>Remove all patterns to reduce</span>
          </div>
        }
      />
    </div>
  );
}

const styles = StyleSheet.create({
  deleteButton: {
    color: GRAY,
    ':hover': {
      color: RED,
    },
    cursor: 'pointer',
  },
  buttonText: {
    marginLeft: 4,
  },
  deleteAll: {
    float: 'right',
  },
  tableCell: {
    textAlign: 'center',
  },
  centeredTableHeader: {
    textAlign: 'center',
  },
  warningIcon: {
    color: ORANGE,
  },
  validation: {
    textAlign: 'center',
    width: 24,
    maxWidth: 24,
  },
});
